/**
 * 🛡️ [Phase 61 — 2026-06-17] Helpers purs pour la cohérence
 * « Liste équipe » ↔ « Présence par jour » dans le formulaire BT.
 *
 * Source unique de vérité = MATRICE (`presence_par_jour` / `dates_assignees`).
 * La liste à gauche (chips) est dérivée en temps réel via ces helpers.
 *
 * Les fonctions sont **pures** (pas d'effet React) pour être testables en
 * isolation par Jest et réutilisables côté handlers du composant.
 */

/**
 * Indique si un employé doit apparaître dans la liste « Membres de l'équipe »
 * (chips à gauche).
 *
 * Règle Phase 61 :
 *   - jamais touché (pas d'entrée dans `employes_assignes`) → VISIBLE
 *     (présent implicitement sur toutes les dates par défaut)
 *   - entry legacy sans `dates_assignees` défini → VISIBLE (legacy implicite)
 *   - `dates_assignees = []` (décoché explicitement partout) → CACHÉ
 *   - `dates_assignees = [un ou plusieurs jours]` → VISIBLE
 *
 * Cette règle garantit que dès que l'utilisatrice décoche la dernière case
 * d'un employé dans la matrice, sa chip disparaît automatiquement de la
 * liste à gauche (cohérence visuelle en temps réel, sans aller-retour réseau).
 *
 * @param {string} employee_id
 * @param {Array<{employee_id: string, dates_assignees?: any}>} employes_assignes
 * @returns {boolean}
 */
export function estVisibleDansListeEquipe(employee_id, employes_assignes) {
  const found = (employes_assignes || []).find(e => e.employee_id === employee_id);
  if (!found) return true; // implicite par défaut
  if (Array.isArray(found.dates_assignees)) {
    return found.dates_assignees.length > 0;
  }
  return true; // legacy non-array → implicite tous jours
}

/**
 * Retourne les jours cochés pour un employé (utilisé par le dialog de
 * confirmation Phase 61 pour informer l'utilisatrice avant suppression).
 *
 * @param {string} employee_id
 * @param {Array<{employee_id: string, dates_assignees?: any}>} employes_assignes
 * @param {Array<string>} dates_chantier — fallback legacy implicite
 * @returns {Array<string>} dates ISO triées
 */
export function getJoursCochesPourEmploye(employee_id, employes_assignes, dates_chantier) {
  const found = (employes_assignes || []).find(e => e.employee_id === employee_id);
  if (!found) {
    // Implicite par défaut → toutes les dates du chantier
    return [...(dates_chantier || [])].sort();
  }
  if (Array.isArray(found.dates_assignees)) {
    return [...found.dates_assignees].sort();
  }
  // Legacy : dates_assignees absent/undefined → toutes les dates du chantier
  return [...(dates_chantier || [])].sort();
}

/**
 * Construit la prochaine version de `employes_assignes` après suppression
 * d'un membre OFFICIEL (équipe du chef principal) : vide ses cases
 * sans le retirer du tableau (préserve l'entrée DB).
 *
 * Utilisé par le handler `supprimerMembreOfficiel` du composant.
 *
 * @param {string} employee_id
 * @param {Array} employes_assignes
 * @returns {Array}
 */
export function viderJoursMembreOfficiel(employee_id, employes_assignes) {
  return (employes_assignes || []).map(e =>
    e.employee_id === employee_id ? { ...e, dates_assignees: [] } : e
  );
}

/**
 * Construit la prochaine version de `employes_assignes` après suppression
 * d'un membre EXTRA (peintre d'une autre équipe ou sous-traitant) : retire
 * complètement son entrée (l'extra n'a pas vocation à rester en DB s'il
 * n'est plus dans le BT).
 *
 * Utilisé par le handler `supprimerMembreExtra` du composant.
 *
 * @param {string} employee_id
 * @param {Array} employes_assignes
 * @returns {Array}
 */
export function retirerMembreExtra(employee_id, employes_assignes) {
  return (employes_assignes || []).filter(e => e.employee_id !== employee_id);
}
